import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Wrench, ArrowRight, TrendUp, Package, Coins } from "@phosphor-icons/react";
import RupiahInput from "@/components/RupiahInput";
import CarExpensesDialog from "@/components/CarExpensesDialog";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function Cars() {
  const [items, setItems] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [tab, setTab] = useState("stock"); // stock | sold
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseCar, setExpenseCar] = useState(null);

  // Stock filters
  const [fBrand, setFBrand] = useState("all");
  const [fYear, setFYear] = useState("all");
  const [fColor, setFColor] = useState("all");
  const [fType, setFType] = useState("all");

  // Sold filters (month YYYY-MM, "all" for all months)
  const now = new Date();
  const [soldMonth, setSoldMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [soldSaleType, setSoldSaleType] = useState("all"); // all | cash | credit

  const emptyForm = { brand: "", model: "", type: "", year: new Date().getFullYear(), plate_number: "", color: "", price: "", account_number: "", transmission: "", image_url: "", description: "" };
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/cars").then((r) => setItems(r.data));
  const loadSales = () => api.get("/sales").then((r) => setSales(r.data));
  const loadPurchases = () => api.get("/purchases").then((r) => setPurchases(r.data));
  const loadCatalog = () => api.get("/car-catalog").then((r) => setCatalog(r.data.grouped || {}));

  useEffect(() => { load(); loadSales(); loadPurchases(); loadCatalog(); }, []);

  const purchasePriceByCar = useMemo(() => {
    const m = {};
    for (const p of purchases) { if (p.car_id) m[p.car_id] = Number(p.purchase_price || 0); }
    return m;
  }, [purchases]);

  const carById = useMemo(() => {
    const m = {}; for (const c of items) m[c.id] = c; return m;
  }, [items]);

  // --- STOCK TAB (available) ---
  const stock = useMemo(() => items.filter((c) => c.status !== "sold"), [items]);
  const brandsInStock = useMemo(() => Array.from(new Set(stock.map((c) => c.brand).filter(Boolean))).sort(), [stock]);
  const yearsInStock = useMemo(() => Array.from(new Set(stock.map((c) => c.year).filter(Boolean))).sort((a, b) => b - a), [stock]);
  const colorsInStock = useMemo(() => Array.from(new Set(stock.map((c) => c.color).filter(Boolean))).sort(), [stock]);
  const typesInStock = useMemo(() => Array.from(new Set(stock.map((c) => c.type).filter(Boolean))).sort(), [stock]);

  const stockFiltered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = stock;
    if (fBrand !== "all") list = list.filter((c) => c.brand === fBrand);
    if (fYear !== "all") list = list.filter((c) => String(c.year) === String(fYear));
    if (fColor !== "all") list = list.filter((c) => c.color === fColor);
    if (fType !== "all") list = list.filter((c) => c.type === fType);
    if (s) {
      list = list.filter((c) =>
        (c.brand || "").toLowerCase().includes(s) ||
        (c.model || "").toLowerCase().includes(s) ||
        (c.type || "").toLowerCase().includes(s) ||
        (c.plate_number || "").toLowerCase().includes(s) ||
        (c.color || "").toLowerCase().includes(s) ||
        (c.account_number || "").toLowerCase().includes(s) ||
        String(c.year || "").includes(s)
      );
    }
    // Sort by brand (A-Z), then model (A-Z), then year (newest first)
    return [...list].sort((a, b) => {
      const ba = (a.brand || "").localeCompare(b.brand || "", "id");
      if (ba !== 0) return ba;
      const mo = (a.model || "").localeCompare(b.model || "", "id");
      if (mo !== 0) return mo;
      return (b.year || 0) - (a.year || 0);
    });
  }, [stock, q, fBrand, fYear, fColor, fType]);

  // --- SOLD TAB ---
  const soldRows = useMemo(() => {
    return sales.map((s) => {
      const car = s.car || carById[s.car_id] || {};
      const purchase = purchasePriceByCar[s.car_id] || 0;
      const expenses = Number(car.total_expense || 0);
      const commission = Number(s.makelar_commission || 0);
      const sold = Number(s.total_price || 0);
      const cost = purchase + expenses + commission;
      const margin = sold - cost;
      return { sale: s, car, purchase, expenses, commission, sold, cost, margin };
    });
  }, [sales, carById, purchasePriceByCar]);

  const soldFiltered = useMemo(() => {
    let list = soldRows;
    if (soldMonth !== "all") {
      list = list.filter((r) => (r.sale.sale_date || "").startsWith(soldMonth));
    }
    if (soldSaleType !== "all") {
      list = list.filter((r) => r.sale.sale_type === soldSaleType);
    }
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((r) =>
        (r.car.brand || "").toLowerCase().includes(s) ||
        (r.car.model || "").toLowerCase().includes(s) ||
        (r.car.plate_number || "").toLowerCase().includes(s) ||
        (r.sale.account_number || "").toLowerCase().includes(s) ||
        (r.sale.customer?.name || "").toLowerCase().includes(s)
      );
    }
    return [...list].sort((a, b) => (b.sale.sale_date || "").localeCompare(a.sale.sale_date || ""));
  }, [soldRows, soldMonth, soldSaleType, q]);

  const soldTotals = useMemo(() => {
    const totalSold = soldFiltered.reduce((a, r) => a + r.sold, 0);
    const totalCost = soldFiltered.reduce((a, r) => a + r.cost, 0);
    const totalMargin = soldFiltered.reduce((a, r) => a + r.margin, 0);
    return { count: soldFiltered.length, totalSold, totalCost, totalMargin };
  }, [soldFiltered]);

  // Available months in sold data (for month picker)
  const soldMonthOptions = useMemo(() => {
    const set = new Set(soldRows.map((r) => (r.sale.sale_date || "").slice(0, 7)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [soldRows]);

  // Form / catalog helpers
  const brandsCat = useMemo(() => Object.keys(catalog).sort(), [catalog]);
  const modelsCat = useMemo(() => (catalog[form.brand] ? Object.keys(catalog[form.brand]).sort() : []), [catalog, form.brand]);
  const typesCat = useMemo(() => (catalog[form.brand]?.[form.model] || []), [catalog, form.brand, form.model]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyForm, ...it, type: it.type || "", price: String(it.price) }); setOpen(true); };

  const submit = async () => {
    try {
      const payload = { ...form, year: parseInt(form.year), price: parseFloat(form.price) };
      if (editing) await api.put(`/cars/${editing.id}`, payload);
      else await api.post("/cars", payload);
      toast.success("Tersimpan");
      setOpen(false); load(); loadCatalog();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus mobil?")) return;
    await api.delete(`/cars/${id}`); toast.success("Terhapus"); load();
  };

  const resetStockFilters = () => { setFBrand("all"); setFYear("all"); setFColor("all"); setFType("all"); };

  return (
    <div>
      <PageHeader
        title="Mobil"
        subtitle="Inventori: stok tersedia dan mobil yang sudah laku"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-car-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Tambah Mobil
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-xl max-w-[95vw] max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Tambah"} Mobil</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">No. Rekening <span className="text-gray-400 normal-case">(manual — melekat pada mobil)</span></Label>
                  <Input value={form.account_number || ""} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="Contoh: 1501" className="mt-2 rounded-none font-mono-num" data-testid="car-account" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Merek</Label>
                  <Input list="car-brands" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value, model: "", type: "" })} className="mt-2 rounded-none" data-testid="car-brand" placeholder="Pilih atau ketik merek" />
                  <datalist id="car-brands">{brandsCat.map((b) => <option key={b} value={b} />)}</datalist>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Model</Label>
                  <Input list="car-models" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value, type: "" })} className="mt-2 rounded-none" data-testid="car-model" placeholder="Pilih atau ketik model" />
                  <datalist id="car-models">{modelsCat.map((m) => <option key={m} value={m} />)}</datalist>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Type / Varian</Label>
                  <Input list="car-types" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-2 rounded-none" data-testid="car-type" placeholder="Contoh: Veloz 1.5 AT" />
                  <datalist id="car-types">{typesCat.map((t) => <option key={t} value={t} />)}</datalist>
                </div>
                <div><Label className="text-xs uppercase tracking-wider">Tahun</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="car-year" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Plat</Label><Input value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Warna</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-2 rounded-none" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Harga</Label><RupiahInput value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-2 rounded-none" data-testid="car-price" /></div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Transmisi</Label>
                  <Select value={form.transmission || ""} onValueChange={(v) => setForm({ ...form, transmission: v })}>
                    <SelectTrigger data-testid="car-transmission" className="mt-2 rounded-none"><SelectValue placeholder="Pilih transmisi" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MT">Manual (MT)</SelectItem>
                      <SelectItem value="AT">Matic (AT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Foto Mobil <span className="text-gray-400 normal-case">(1 gambar, maks 500KB)</span></Label>
                  <div className="mt-2 flex items-start gap-4">
                    <div className="w-32 h-24 border border-gray-200 bg-gray-50 flex items-center justify-center shrink-0 overflow-hidden">
                      {form.image_url ? <img src={form.image_url} alt="mobil" className="w-full h-full object-cover" /> : <div className="text-xs text-gray-400">No image</div>}
                    </div>
                    <div className="flex-1">
                      <label className="inline-flex items-center gap-2 border border-gray-900 px-3 py-2 cursor-pointer hover:bg-gray-900 hover:text-white transition-colors text-xs uppercase tracking-wider" data-testid="car-image-label">
                        <Plus size={12} /> {form.image_url ? "Ganti Foto" : "Upload Foto"}
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="car-image-input" onChange={(e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          if (f.size > 500 * 1024) { toast.error("Ukuran gambar maks 500KB"); return; }
                          const rd = new FileReader(); rd.onload = (ev) => setForm({ ...form, image_url: ev.target.result }); rd.readAsDataURL(f);
                        }} />
                      </label>
                      {form.image_url && (
                        <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="ml-3 text-xs uppercase tracking-wider text-[#E63946] hover:underline">Hapus</button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Deskripsi <span className="text-gray-400 normal-case">(tampil di katalog publik)</span></Label>
                  <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-2 w-full border border-gray-300 p-2 text-sm rounded-none" placeholder="Kondisi mulus, service record lengkap, dll." data-testid="car-description" />
                </div>
              </div>
              <Button onClick={submit} data-testid="car-submit" className="mt-4 w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan</Button>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 md:p-6 lg:p-10">
        {/* Tabs */}
        <div className="flex gap-0 border border-gray-200 -mb-px">
          <button
            onClick={() => { setTab("stock"); setQ(""); }}
            data-testid="tab-stock"
            className={`px-6 py-4 text-xs uppercase tracking-wider border-r border-gray-200 flex items-center gap-2 ${tab === "stock" ? "bg-[#0A0A0A] text-white font-semibold" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            <Package size={14} weight="bold" /> Stok Tersedia
            <span className="ml-1 font-mono-num text-[10px] opacity-70">({stock.length})</span>
          </button>
          <button
            onClick={() => { setTab("sold"); setQ(""); }}
            data-testid="tab-sold"
            className={`px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 ${tab === "sold" ? "bg-[#0A0A0A] text-white font-semibold" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            <TrendUp size={14} weight="bold" /> Sudah Laku
            <span className="ml-1 font-mono-num text-[10px] opacity-70">({sales.length})</span>
          </button>
        </div>

        {tab === "stock" ? (
          <div className="border border-gray-200 p-6 bg-white">
            {/* Stock filters */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
              <div className="md:col-span-5">
                <SearchBar value={q} onChange={setQ} placeholder="Cari merek / model / plat / rekening..." testid="stock-search" count={stockFiltered.length} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Merek</Label>
                <Select value={fBrand} onValueChange={setFBrand}>
                  <SelectTrigger data-testid="filter-brand" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Merek</SelectItem>
                    {brandsInStock.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Tahun</Label>
                <Select value={fYear} onValueChange={setFYear}>
                  <SelectTrigger data-testid="filter-year" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Tahun</SelectItem>
                    {yearsInStock.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Type / Varian</Label>
                <Select value={fType} onValueChange={setFType}>
                  <SelectTrigger data-testid="filter-type" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Type</SelectItem>
                    {typesInStock.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Warna</Label>
                <Select value={fColor} onValueChange={setFColor}>
                  <SelectTrigger data-testid="filter-color" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Warna</SelectItem>
                    {colorsInStock.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetStockFilters}
                  data-testid="reset-filters"
                  className="w-full h-10 text-xs uppercase tracking-wider border border-gray-300 hover:bg-gray-50"
                >
                  Reset Filter
                </button>
              </div>
            </div>

            <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="p-4">No. Rekening</th>
                    <th className="p-4">Merek / Model</th>
                    <th className="p-4">Type</th>
                    <th className="p-4">Tahun</th>
                    <th className="p-4">Plat</th>
                    <th className="p-4 text-right">Harga</th>
                    <th className="p-4 text-right">Biaya</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {stockFiltered.map((it) => (
                    <tr key={it.id} className="border-b border-gray-100" data-testid={`stock-row-${it.id}`}>
                      <td className="p-4 font-mono-num font-semibold text-xs">{it.account_number || "-"}</td>
                      <td className="p-4 font-semibold">{it.brand} {it.model}<div className="text-xs text-gray-500 font-normal">{it.color}</div></td>
                      <td className="p-4 text-gray-700 text-xs">{it.type || "-"}</td>
                      <td className="p-4 font-mono-num text-xs">{it.year}</td>
                      <td className="p-4 font-mono-num text-xs">{it.plate_number || "-"}</td>
                      <td className="p-4 text-right font-mono-num">{formatIDR(it.price)}</td>
                      <td className="p-4 text-right font-mono-num text-xs">
                        <button
                          onClick={() => { setExpenseCar(it); setExpenseOpen(true); }}
                          data-testid={`car-expense-btn-${it.id}`}
                          className="inline-flex items-center gap-1 hover:text-[#F97316] transition-colors"
                          title="Kelola biaya mobil"
                        >
                          <Wrench size={12} weight="bold" />
                          <span className="font-semibold">{it.total_expense ? formatIDR(it.total_expense) : "Tambah"}</span>
                          {it.expense_count > 0 && <span className="text-gray-400">({it.expense_count})</span>}
                        </button>
                      </td>
                      <td className="p-4 text-right">
                        <button onClick={() => openEdit(it)} className="mr-3 text-gray-500 hover:text-black"><PencilSimple size={16} /></button>
                        <button onClick={() => del(it.id)} className="text-gray-500 hover:text-[#E63946]"><Trash size={16} /></button>
                      </td>
                    </tr>
                  ))}
                  {stockFiltered.length === 0 && (
                    <tr><td colSpan="8" className="p-10 text-center text-sm text-gray-500">{stock.length === 0 ? "Belum ada stok" : "Tidak ada hasil"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 p-6 bg-white">
            {/* Sold filters + KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Bulan</Label>
                <Select value={soldMonth} onValueChange={setSoldMonth}>
                  <SelectTrigger data-testid="sold-month-filter" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Bulan</SelectItem>
                    {soldMonthOptions.map((m) => {
                      const [y, mm] = m.split("-");
                      return <SelectItem key={m} value={m}>{MONTHS_ID[parseInt(mm) - 1]} {y}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Tipe Penjualan</Label>
                <Select value={soldSaleType} onValueChange={setSoldSaleType}>
                  <SelectTrigger data-testid="sold-type-filter" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit">Kredit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Cari</Label>
                <div className="mt-1"><SearchBar value={q} onChange={setQ} placeholder="Merek / model / plat / rekening / pelanggan..." testid="sold-search" count={soldFiltered.length} /></div>
              </div>
            </div>

            <div className="flex justify-end mb-3">
              <button
                onClick={async () => {
                  try {
                    const targetMonth = soldMonth !== "all" ? soldMonth : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                    const r = await api.get(`/reports/monthly-sales.xlsx?month=${targetMonth}`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([r.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `rekap-penjualan-${targetMonth}.xlsx`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success("Rekap penjualan diunduh");
                  } catch { toast.error("Gagal mengunduh Excel"); }
                }}
                data-testid="export-rekap-btn"
                className="h-10 px-4 bg-[#10B981] hover:bg-[#0d966b] text-white uppercase tracking-wider text-xs font-semibold flex items-center gap-2"
              >
                Export Rekap Bulan Ini
              </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-gray-200 -m-px mb-6">
              <div className="border border-gray-200 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                  <Package size={12} weight="bold" /> Mobil Laku
                </div>
                <div className="font-mono-num text-2xl font-bold mt-2" data-testid="kpi-sold-count">{soldTotals.count}</div>
                <div className="text-[11px] text-gray-500">unit terjual</div>
              </div>
              <div className="border border-gray-200 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                  <Coins size={12} weight="bold" /> Total Penjualan
                </div>
                <div className="font-mono-num text-2xl font-bold mt-2" data-testid="kpi-total-sold">{formatIDR(soldTotals.totalSold)}</div>
              </div>
              <div className="border border-gray-200 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                  Total HPP + Biaya + Komisi
                </div>
                <div className="font-mono-num text-2xl font-bold mt-2" data-testid="kpi-total-cost">{formatIDR(soldTotals.totalCost)}</div>
              </div>
              <div className="border border-gray-200 p-5 bg-emerald-50">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                  <TrendUp size={12} weight="bold" /> Total Margin
                </div>
                <div className="font-mono-num text-2xl font-bold mt-2 text-[#10B981]" data-testid="kpi-total-margin">{formatIDR(soldTotals.totalMargin)}</div>
                <div className="text-[11px] text-gray-500 font-mono-num">
                  {soldTotals.totalSold ? ((soldTotals.totalMargin / soldTotals.totalSold) * 100).toFixed(1) : 0}% dari penjualan
                </div>
              </div>
            </div>

            <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="p-4">Tanggal</th>
                    <th className="p-4">No. Rek</th>
                    <th className="p-4">Mobil</th>
                    <th className="p-4">Pelanggan</th>
                    <th className="p-4">Tipe</th>
                    <th className="p-4 text-right">Jual</th>
                    <th className="p-4 text-right">HPP</th>
                    <th className="p-4 text-right">Biaya</th>
                    <th className="p-4 text-right">Komisi</th>
                    <th className="p-4 text-right">Margin</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {soldFiltered.map((r) => (
                    <tr key={r.sale.id} className="border-b border-gray-100" data-testid={`sold-row-${r.sale.id}`}>
                      <td className="p-4 font-mono-num text-xs">{formatDate(r.sale.sale_date)}</td>
                      <td className="p-4 font-mono-num font-semibold text-xs">{r.sale.account_number || "-"}</td>
                      <td className="p-4">
                        <div className="font-semibold">{r.car.brand} {r.car.model}</div>
                        <div className="text-xs text-gray-500 font-mono-num">{r.car.year} · {r.car.plate_number || "-"} · {r.car.color || "-"}</div>
                      </td>
                      <td className="p-4 text-xs">{r.sale.customer?.name || "-"}</td>
                      <td className="p-4">
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider ${r.sale.sale_type === "cash" ? "bg-gray-900 text-white" : "border border-gray-900"}`}>
                          {r.sale.sale_type}
                        </span>
                      </td>
                      <td className="p-4 text-right font-mono-num">{formatIDR(r.sold)}</td>
                      <td className="p-4 text-right font-mono-num text-xs text-gray-600">{formatIDR(r.purchase)}</td>
                      <td className="p-4 text-right font-mono-num text-xs text-gray-600">{formatIDR(r.expenses)}</td>
                      <td className="p-4 text-right font-mono-num text-xs text-gray-600">{formatIDR(r.commission)}</td>
                      <td className="p-4 text-right font-mono-num font-bold" style={{ color: r.margin >= 0 ? "#10B981" : "#E63946" }} data-testid={`margin-${r.sale.id}`}>
                        {formatIDR(r.margin)}
                      </td>
                      <td className="p-4 text-right">
                        <Link to={`/sales/${r.sale.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider hover:underline">
                          Detail <ArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {soldFiltered.length === 0 && (
                    <tr><td colSpan="11" className="p-10 text-center text-sm text-gray-500">Tidak ada mobil laku untuk filter ini</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-gray-500 mt-3 font-mono-num">
              Margin = Harga Jual − HPP (harga beli) − Biaya mobil − Komisi makelar
            </div>
          </div>
        )}
      </div>

      <CarExpensesDialog
        car={expenseCar}
        open={expenseOpen}
        onOpenChange={setExpenseOpen}
        onChange={load}
      />
    </div>
  );
}
